import { InputAdornment, TextField } from '@mui/material';
import { housingInfoMsgCode } from 'app/models/Placement/PlacementMods';
import React, { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentModDetails,
  selectIsEditMod,
  selectIsUndoModal,
  selectModSavedStatus,
} from 'store/redux-store/placement-mods/selectors';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { validDecimalAndLength } from '../../ModificationsUtils';

export const AmountToTraveler = () => {
  const { promiseInProgress: isSaveModApiCalling } = usePromiseTracker({ area: 'save-mod-api', delay: 0 });
  const { control, getValues, setValue, clearErrors, setError } = useFormContext();
  const amountToTraveler = 'amountToTraveler';
  const amountToTravelerWatch: string = useWatch({ name: amountToTraveler });
  const actualHousingType: string = useWatch({ name: 'actualHousingType' });
  const currentModDetails = useSelector(selectCurrentModDetails);
  const isModEdit = useSelector(selectIsEditMod);
  const dispatch = useDispatch();
  const isUndoModal = useSelector(selectIsUndoModal);
  const modSavedResponse = useSelector(selectModSavedStatus);
  const { t } = useTranslation();

  const handleChange = (value: string) => {
    setValue(amountToTraveler, value);
    if (value) clearErrors(amountToTraveler);

    // flip back tp false (remove reocord) banner message shown status
    if (parseFloat(value) > 0) {
      const storage = JSON.parse(localStorage.getItem('housingBannerMsg'));
      if (storage && storage.length > 0) {
        for (let index = 0; index < storage.length; index++) {
          const element = storage[index];
          if (element.travelerOptionId === currentModDetails?.travelerOptionId) {
            storage.splice(index, 1);
          }
        }
        localStorage.setItem('housingBannerMsg', JSON.stringify(storage));
        dispatch(placementModsAction.setHousingBannerDisplay(false));
      }
    }
  };

  useEffect(() => {
    if (getValues(amountToTraveler)) {
      clearErrors(amountToTraveler);
    } else {
      setError(amountToTraveler, {});
    }
    if (isUndoModal) {
      dispatch(placementModsAction.setIsUndoModal(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUndoModal]);

  const messageDisplayForCurrentMod = () => {
    const storage = JSON.parse(localStorage.getItem('housingBannerMsg'));
    let status: boolean = true;
    if (storage && storage.length > 0) {
      for (let index = 0; index < storage.length; index++) {
        const element = storage[index];
        if (element.travelerOptionId === currentModDetails?.travelerOptionId) {
          status = false;
        }
      }
    }
    return status;
  };

  useEffect(() => {
    if (actualHousingType === 'Subsidy' && parseFloat(amountToTravelerWatch) === 0 && messageDisplayForCurrentMod()) {
      dispatch(
        placementModsAction.setModSavedStatus({
          ...modSavedResponse,
          message: t('placement.placementMods.housingInfoMsg'),
          responseCode: 20,
        }),
      );
    } else {
      if (modSavedResponse?.responseCode === housingInfoMsgCode) {
        dispatch(placementModsAction.setModSavedStatus(null));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualHousingType, amountToTravelerWatch]);

  return (
    <Controller
      control={control}
      name={amountToTraveler}
      rules={{
        required: true,
      }}
      render={({ field: { onChange, value, ...rest } }) => (
        <TextField
          variant="filled"
          fullWidth
          id="amountToTraveler"
          color="primary"
          autoComplete="off"
          label={t('placement.placementMods.amountToTraveler')}
          InputProps={{
            readOnly: !isModEdit,
            style: {
              pointerEvents: !isModEdit ? 'none' : 'auto',
            },
            startAdornment: <>{value && <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>}</>,
          }}
          value={getValues(amountToTraveler) ?? ''}
          onChange={e => {
            if (validDecimalAndLength(e.target.value, { numberLength: 6, decimalLength: 2 })) {
              const validInput = e.target.value.replace(/^\./, '0.');

              handleChange(validInput);
              onChange(validInput);
            }
          }}
          InputLabelProps={{
            shrink: amountToTravelerWatch ? true : false,
          }}
          disabled={isSaveModApiCalling}
          {...rest}
        />
      )}
    />
  );
};
